import React, { useRef, useState, useEffect } from 'react';
import Modal from '../Modal';
import Button from '../../Button';
import useToast from '../../../hooks/useToast';
import { useUserStore } from '../../../store/user';
import { documentsService } from '../../../services/documents.service';
import { FaShieldHalved, FaFilePdf, FaCheck, FaTriangleExclamation } from 'react-icons/fa6';

export interface ModalBatchSignHandle {
  openModal: (selectedDocs: { _id: string; name: string; isFolder: boolean }[]) => void;
  closeModal: () => void;
}

export interface ModalBatchSignProps {
  refetch: () => void;
}

type ModalRef = React.ElementRef<typeof Modal>;

const ModalBatchSign: React.ForwardRefRenderFunction<ModalBatchSignHandle, ModalBatchSignProps> = (
  { refetch },
  ref,
) => {
  const modalRef = useRef<ModalRef>(null);
  const toast = useToast();
  const { user } = useUserStore();

  const [selectedDocs, setSelectedDocs] = useState<{ _id: string; name: string; isFolder: boolean }[]>([]);
  const [certInfo, setCertInfo] = useState<any>(null);
  const [declared, setDeclared] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [reason, setReason] = useState('Conferência com original físico conforme Decreto nº 10.278/2020 e Lei nº 12.682/2012');

  React.useImperativeHandle(ref, () => ({
    openModal(docs) {
      // Filter only files (no folders)
      const validDocs = docs.filter(d => !d.isFolder);
      if (validDocs.length === 0) {
        toast.errorToast('Selecione pelo menos um documento (arquivos PDF) para assinar.');
        return;
      }
      setSelectedDocs(validDocs);
      setDeclared(false);
      modalRef.current?.openModal();
      loadCertStatus();
    },
    closeModal() {
      modalRef.current?.closeModal();
    },
  }));

  const loadCertStatus = async () => {
    try {
      const res = await documentsService.getCertificateStatus();
      if (res?.data) {
        setCertInfo(res.data);
      }
    } catch (err) {
      console.warn('Erro ao carregar status do certificado:', err);
    }
  };

  const handleConfirmSign = async () => {
    if (!declared) {
      toast.errorToast('É obrigatório confirmar a declaração de conferência com o original.');
      return;
    }

    try {
      setIsLoading(true);
      const docIds = selectedDocs.map(d => d._id);
      const res = await documentsService.signBatchDocuments(docIds, reason);

      if (res?.data) {
        const { signedCount, failedCount } = res.data;
        if (failedCount === 0) {
          toast.successToast(`${signedCount} documento(s) conferido(s) e assinado(s) com sucesso com ICP-Brasil!`);
        } else {
          toast.successToast(`${signedCount} assinado(s) com sucesso, ${failedCount} com falha.`);
        }
      }

      modalRef.current?.closeModal();
      refetch();
    } catch (err: any) {
      toast.errorToast(err?.response?.data?.message || 'Erro ao processar assinatura dos documentos.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal ref={modalRef} title="Conferência e Assinatura Digital ICP-Brasil (A1)" maxWidth={650}>
      <div style={{ padding: '15px 5px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
        {/* Certificate banner */}
        <div style={{
          background: certInfo?.isDemo ? '#fff8e1' : '#e8f5e9',
          border: `1px solid ${certInfo?.isDemo ? '#ffe082' : '#c8e6c9'}`,
          borderRadius: '8px',
          padding: '12px 16px',
          display: 'flex',
          alignItems: 'center',
          gap: '12px'
        }}>
          <FaShieldHalved size={24} color={certInfo?.isDemo ? '#f57f17' : '#2e7d32'} />
          <div style={{ fontSize: '13px' }}>
            <div style={{ fontWeight: 600, color: certInfo?.isDemo ? '#e65100' : '#1b5e20' }}>
              {certInfo?.isDemo ? 'Certificado ICP-Brasil (Modo Demonstração)' : 'Certificado ICP-Brasil Ativo'}
            </div>
            <div style={{ color: '#333', marginTop: '2px' }}>
              Empresa: <strong>{certInfo?.companyName || 'Empresa Digitalizadora'}</strong>
              {certInfo?.cnpj && <span> • CNPJ: <strong>{certInfo.cnpj}</strong></span>}
            </div>
          </div>
        </div>

        {/* Conferente info */}
        <div style={{
          background: '#f8f9fa',
          border: '1px solid #e9ecef',
          borderRadius: '8px',
          padding: '12px 16px',
          fontSize: '13px',
          color: '#495057'
        }}>
          <div><strong>Responsável pela Conferência:</strong> {user?.name}</div>
          {user?.document && <div style={{ marginTop: '2px' }}><strong>CPF:</strong> {user.document}</div>}
          <div style={{ marginTop: '2px' }}><strong>Perfil:</strong> {user?.role === 'admin' ? 'Administrador' : 'Gerente'}</div>
        </div>

        {/* Selected documents list */}
        <div>
          <div style={{ fontSize: '13px', fontWeight: 600, marginBottom: '6px', color: '#333' }}>
            Documentos a serem assinados ({selectedDocs.length}):
          </div>
          <div style={{
            maxHeight: '130px',
            overflowY: 'auto',
            border: '1px solid #dee2e6',
            borderRadius: '6px',
            padding: '8px 12px',
            background: '#ffffff'
          }}>
            {selectedDocs.map((doc, idx) => (
              <div key={doc._id} style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', padding: '4px 0', borderBottom: idx < selectedDocs.length - 1 ? '1px solid #f1f3f5' : 'none' }}>
                <FaFilePdf color="#e53935" size={14} />
                <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{doc.name}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Legal Declaration */}
        <div style={{
          background: '#f1f8e9',
          border: '1px solid #dcedc8',
          borderRadius: '8px',
          padding: '14px',
          display: 'flex',
          gap: '12px',
          cursor: 'pointer'
        }} onClick={() => setDeclared(!declared)}>
          <input
            type="checkbox"
            id="declarationCheck"
            checked={declared}
            onChange={(e) => setDeclared(e.target.checked)}
            style={{ width: '18px', height: '18px', cursor: 'pointer', marginTop: '2px' }}
          />
          <label htmlFor="declarationCheck" style={{ fontSize: '12px', color: '#2e7d32', cursor: 'pointer', lineHeight: '1.4' }}>
            <strong>Declaração de Conformidade Legal:</strong><br />
            Declaro para os devidos fins que efetuei a conferência deste(s) documento(s) com o respectivo original físico, atestando sua integridade, autenticidade e fidelidade nos termos da <strong>Lei Federal nº 12.682/2012</strong> e do <strong>Decreto Federal nº 10.278/2020</strong>.
          </label>
        </div>

        {/* Buttons */}
        <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '10px' }}>
          <Button
            label="Cancelar"
            color="secondary"
            variantType="block"
            onClick={() => modalRef.current?.closeModal()}
            disabled={isLoading}
          />
          <Button
            label={isLoading ? "Assinando..." : `Conferir e Assinar (${selectedDocs.length})`}
            color="success"
            variantType="block"
            onClick={handleConfirmSign}
            disabled={!declared || isLoading}
          />
        </div>
      </div>
    </Modal>
  );
};

export default React.forwardRef(ModalBatchSign);
